import type { LucideIcon } from 'lucide-react';
import { isCertificationsStaticPath } from '../../constants/management2Certifications';
import { isFlowStakeholdersStaticPath } from '../../constants/flowStakeholders';
import { VMS_ADMIN_CONFIG_ROUTES } from '../../constants/vmsAdminConfigNav';
import { isVmsContractManagementPath, isVmsDpiaManagementPath } from '../../utils/vmsRole';

export const PRE_ONBOARDING_GROUP = 'Pre Onboarding';
export const ONBOARDING_GROUP = 'Onboarding';

/** User-facing module name for `/vms/ongoing` (vendor reviews & assessment batches). */
export const ONGOING_MANAGEMENT_LABEL = 'Ongoing Management';

export const ONGOING_VENDOR_PERFORMANCE_REVIEW_LABEL = 'Vendor Performance Review';
export const ONGOING_PERIODIC_REVIEW_LABEL = 'Vendor Periodic Review';

/** Flat links shown in the primary nav strip with Pre/Onboarding/Ongoing flyouts (maroon `|` separators). */
export const VMS_NAV_ONBOARDING_STRIP_LINK_NAMES = new Set([
  ONGOING_MANAGEMENT_LABEL,
  ONGOING_VENDOR_PERFORMANCE_REVIEW_LABEL,
  ONGOING_PERIODIC_REVIEW_LABEL,
  'ongoing2',
  ONBOARDING_GROUP,
]);

export type VmsNavSublink = { name: string; href: string };
export type VmsNavChild = { name: string; href: string; icon: LucideIcon; sublinks?: VmsNavSublink[] };
export type VmsNavItem =
  | ({ type: 'link' } & VmsNavChild)
  | { type: 'group'; name: string; icon: LucideIcon; children: VmsNavChild[] };

function parseLocationSearch(search: string): URLSearchParams {
  const raw = search.startsWith('?') ? search.slice(1) : search;
  return new URLSearchParams(raw);
}

/** True when the route is under the Ongoing Management vendor reviews hub. */
export function isOngoingManagementRouteActive(location: { pathname: string; search: string }): boolean {
  const path = location.pathname.replace(/\/$/, '');
  if (path === '/vms/ongoing' || path.startsWith('/vms/ongoing/')) return true;
  if (path === '/vms/ongoing-management' || path.startsWith('/vms/ongoing2')) return true;
  return false;
}

function matchOngoingHubNavHref(
  location: { pathname: string; search: string },
  href: string,
): boolean | null {
  if (!href.includes('/vms/ongoing?hub=')) return null;
  if (!isOngoingManagementRouteActive(location)) return false;
  const hub = href.includes('hub=performance')
    ? 'performance'
    : href.includes('hub=periodic')
      ? 'periodic'
      : null;
  if (!hub) return false;
  const sp = parseLocationSearch(location.search);
  const assessment = sp.get('assessment') ?? sp.get('assessment_type');
  if (hub === 'performance') {
    return sp.get('hub') === 'performance' || assessment === 'monthly';
  }
  return sp.get('hub') === 'periodic' || assessment === 'periodic';
}

export function isNavLinkActive(pathname: string, href: string): boolean {
  if (pathname === href) return true;
  const norm = href.replace(/\/$/, '');
  if (norm === '/vms/vendor-initiation' && pathname.startsWith('/vms/vendor-initiation-requests')) {
    return false;
  }
  if (
    norm === '/vms/vendor-initiation-requests' &&
    (pathname === '/vms/pre-onboarding/vendor-initiation' ||
      pathname.startsWith('/vms/pre-onboarding/vendor-initiation/'))
  ) {
    return false;
  }
  if (
    norm === '/vms/pre-onboarding/vendor-initiation' &&
    (pathname === '/vms/vendor-initiation-requests' ||
      pathname.startsWith('/vms/vendor-initiation-requests/'))
  ) {
    return false;
  }
  if (pathname.startsWith(`${norm}/`)) return true;
  if (
    href === '/vms/vendor-proposals/stakeholder' &&
    /^\/vms\/vendor-proposals\/\d+(?:\/|$)/.test(pathname)
  ) {
    return true;
  }
  return false;
}

/** Match `href` to current route; supports query strings (e.g. annexure deep links). */
export function isNavLinkActiveLocation(
  location: { pathname: string; search: string },
  href: string,
): boolean {
  const ongoingHub = matchOngoingHubNavHref(location, href);
  if (ongoingHub !== null) return ongoingHub;

  const q = href.indexOf('?');
  if (q !== -1) {
    const path = href.slice(0, q);
    const search = href.slice(q);
    return location.pathname === path && (location.search || '') === search;
  }
  return isNavLinkActive(location.pathname, href);
}

export function navGroupChildActive(
  location: { pathname: string; search: string },
  child: VmsNavChild,
): boolean {
  if (isNavLinkActiveLocation(location, child.href)) return true;
  return Boolean(child.sublinks?.some((s) => isNavLinkActiveLocation(location, s.href)));
}

/** True when the current route is a Management flyout child (nav link or known static module). */
export function isManagementFlyoutRouteActive(
  location: { pathname: string; search: string },
  flyoutLinks: Array<{ href: string }>,
): boolean {
  if (flyoutLinks.some((link) => isNavLinkActiveLocation(location, link.href))) {
    return true;
  }
  return isManagementSectionStaticPath(location.pathname);
}

/** Management flyout routes and related static modules (hide active-service strip on these pages). */
export function isVmsManagementMenuPath(pathname: string): boolean {
  return (
    isCertificationsStaticPath(pathname) ||
    isFlowStakeholdersStaticPath(pathname) ||
    isVmsContractManagementPath(pathname) ||
    isVmsDpiaManagementPath(pathname) ||
    pathname === '/vms/vendor-exit' ||
    pathname.startsWith('/vms/vendor-exit/') ||
    pathname === '/vms/audit-logs' ||
    pathname.startsWith('/vms/audit-logs/') ||
    pathname === '/vms/registered-vendors' ||
    pathname.startsWith('/vms/registered-vendors/') ||
    pathname === VMS_ADMIN_CONFIG_ROUTES.annexureManagement ||
    pathname.startsWith(`${VMS_ADMIN_CONFIG_ROUTES.annexureManagement}/`) ||
    pathname === '/vms/annexure-management' ||
    pathname.startsWith('/vms/annexure-management/')
  );
}

function isManagementSectionStaticPath(pathname: string): boolean {
  return isVmsManagementMenuPath(pathname);
}

export function initialOpenGroupsForPath(
  items: VmsNavItem[],
  location: { pathname: string; search: string },
): Record<string, boolean> {
  const o: Record<string, boolean> = {};
  for (const item of items) {
    if (item.type === 'group' && item.children.some((c) => navGroupChildActive(location, c))) {
      o[item.name] = true;
    }
  }
  return o;
}

const MANAGEMENT_LINK_NAMES = new Set([
  'Flow Stakeholders',
  'My flows & stakeholders',
  'Contract Management',
  'DPIA Management',
]);

/** Primary strip links rendered in the secondary nav cluster (e.g. Register, Annexure Management). */
const PRIMARY_NAV_STANDALONE_LINK_NAMES = new Set(['Annexure Management', 'Register', 'Outsourcing register']);

const OTHER_LINK_NAMES = new Set(['Vendor Exit', 'Audit Logs']);

export function partitionVmsNavigation(items: VmsNavItem[]) {
  const top: VmsNavItem[] = [];
  const onboarding: VmsNavItem[] = [];
  const standalone: VmsNavItem[] = [];
  const management: VmsNavItem[] = [];
  const management2: VmsNavItem[] = [];
  const other: VmsNavItem[] = [];

  for (const item of items) {
    if (item.type === 'group') {
      onboarding.push(item);
      continue;
    }
    if (item.type === 'link' && VMS_NAV_ONBOARDING_STRIP_LINK_NAMES.has(item.name)) {
      onboarding.push(item);
      continue;
    }
    if (item.name === 'Dashboard' || item.name === 'My Tray') {
      top.push(item);
      continue;
    }
    if (item.type === 'link' && PRIMARY_NAV_STANDALONE_LINK_NAMES.has(item.name)) {
      standalone.push(item);
      continue;
    }
    if (item.type === 'link' && item.href.startsWith('/vms/management2/')) {
      management2.push(item);
      continue;
    }
    if (MANAGEMENT_LINK_NAMES.has(item.name)) {
      management.push(item);
      continue;
    }
    if (OTHER_LINK_NAMES.has(item.name)) {
      other.push(item);
      continue;
    }
    management.push(item);
  }

  return { top, onboarding, standalone, management, management2, other };
}
